import type { DomainEvent, SimulationState } from '../game/models';

export class AudioSystem {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private engineGain: GainNode | null = null;
  private engineLeft: OscillatorNode | null = null;
  private engineRight: OscillatorNode | null = null;
  private enginePan: StereoPannerNode | null = null;
  private missileGain: GainNode | null = null;
  private missileOscillator: OscillatorNode | null = null;
  private skimmerGain: GainNode | null = null;
  private skimmerOscillator: OscillatorNode | null = null;
  private muted = false;
  private paused = false;
  private volume = 0.72;

  public async unlock(): Promise<void> {
    if (!this.context) this.initialize();
    if (this.context?.state === 'suspended') await this.context.resume();
  }

  public update(state: SimulationState): void {
    const context = this.context;
    if (!context || !this.master || !this.engineGain || !this.engineLeft || !this.engineRight) return;
    const now = context.currentTime;
    const active = state.mode === 'playing' && !this.paused && !this.muted;
    const left = state.player.drive.leftTread;
    const right = state.player.drive.rightTread;
    const load = (Math.abs(left) + Math.abs(right)) / 2;
    this.master.gain.setTargetAtTime(active ? this.activeGain : 0.0001, now, 0.035);
    this.engineGain.gain.setTargetAtTime(active ? 0.018 + load * 0.052 : 0.0001, now, 0.045);
    this.engineLeft.frequency.setTargetAtTime(42 + Math.abs(left) * 27, now, 0.055);
    this.engineRight.frequency.setTargetAtTime(45 + Math.abs(right) * 29, now, 0.055);
    this.enginePan?.pan.setTargetAtTime(Math.max(-0.45, Math.min(0.45, (right - left) * 0.28)), now, 0.06);

    const missileDistance = state.primaryThreat?.kind === 'missile'
      ? Math.hypot(
          state.primaryThreat.position.x - state.player.position.x,
          state.primaryThreat.position.z - state.player.position.z,
        )
      : Number.POSITIVE_INFINITY;
    const missileAmount = active && Number.isFinite(missileDistance)
      ? Math.max(0.015, Math.min(0.13, 0.16 - missileDistance / 600))
      : 0.0001;
    this.missileGain?.gain.setTargetAtTime(missileAmount, now, 0.05);
    this.missileOscillator?.frequency.setTargetAtTime(190 + Math.max(0, 100 - missileDistance) * 1.7, now, 0.04);
    this.skimmerGain?.gain.setTargetAtTime(active && state.skimmer ? 0.055 : 0.0001, now, 0.07);
    this.skimmerOscillator?.frequency.setTargetAtTime(310 + Math.sin(state.elapsed * 9) * 48, now, 0.035);
  }

  public handleEvents(events: readonly DomainEvent[]): void {
    for (const event of events) {
      switch (event.type) {
        case 'player-fired':
          this.playNoise(0.11, 0.23, 1_200);
          this.playTone(118, 54, 0.13, 0.14, 'sawtooth');
          break;
        case 'enemy-fired':
          this.playTone(240, 112, 0.11, 0.1, 'square');
          break;
        case 'target-destroyed':
          this.playNoise(0.26, 0.2, 760);
          this.playTone(94, 38, 0.28, 0.12, 'triangle');
          break;
        case 'player-destroyed':
          this.playNoise(0.48, 0.3, 420);
          this.playTone(72, 24, 0.55, 0.2, 'sawtooth');
          break;
        case 'shell-impact':
        case 'missile-cover-impact':
          this.playNoise(0.09, 0.11, 1_500);
          break;
        case 'bump':
          this.playTone(64, 45, 0.08, 0.065, 'square');
          break;
        case 'bonus-tank':
          this.playArpeggio();
          break;
        case 'radar-contact':
          this.playTone(720, 560, 0.055, 0.045, 'sine');
          break;
        case 'mode-changed':
          if (event.mode === 'game-over') this.playTone(130, 38, 0.9, 0.13, 'triangle');
          break;
      }
    }
  }

  public setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(muted || this.paused ? 0.0001 : this.activeGain, this.context.currentTime, 0.025);
    }
  }

  public toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  public setVolume(volume: number): void {
    this.volume = Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 0.72;
    if (this.master && this.context && !this.muted && !this.paused) {
      this.master.gain.setTargetAtTime(this.activeGain, this.context.currentTime, 0.03);
    }
  }

  public setPaused(paused: boolean): void {
    const wasPaused = this.paused;
    if (paused && !wasPaused) this.playTone(170, 82, 0.12, 0.07, 'square');
    this.paused = paused;
    if (this.master && this.context) {
      const changeTime = this.context.currentTime + (paused && !wasPaused ? 0.075 : 0);
      this.master.gain.setTargetAtTime(paused ? 0.0001 : this.muted ? 0.0001 : this.activeGain, changeTime, 0.03);
    }
    if (!paused && wasPaused) this.playTone(92, 184, 0.1, 0.055, 'square', 0.035);
  }

  public async destroy(): Promise<void> {
    const context = this.context;
    this.context = null;
    if (context && context.state !== 'closed') await context.close();
  }

  public debugSnapshot(): { initialized: boolean; continuousVoices: number; muted: boolean; paused: boolean; masterGain: number } {
    return {
      initialized: this.context !== null,
      continuousVoices: [
        this.engineLeft,
        this.engineRight,
        this.missileOscillator,
        this.skimmerOscillator,
      ].filter((voice) => voice !== null).length,
      muted: this.muted,
      paused: this.paused,
      masterGain: this.master?.gain.value ?? 0,
    };
  }

  private initialize(): void {
    if (typeof AudioContext === 'undefined') return;
    const context = new AudioContext();
    const master = context.createGain();
    master.gain.value = 0.0001;
    master.connect(context.destination);
    this.context = context;
    this.master = master;

    const engineGain = context.createGain();
    const enginePan = context.createStereoPanner();
    engineGain.gain.value = 0.0001;
    engineGain.connect(enginePan).connect(master);
    this.engineGain = engineGain;
    this.enginePan = enginePan;
    this.engineLeft = this.createContinuousOscillator('sawtooth', 42, engineGain);
    this.engineRight = this.createContinuousOscillator('triangle', 45, engineGain);

    const missileGain = context.createGain();
    missileGain.gain.value = 0.0001;
    missileGain.connect(master);
    this.missileGain = missileGain;
    this.missileOscillator = this.createContinuousOscillator('square', 190, missileGain);

    const skimmerGain = context.createGain();
    skimmerGain.gain.value = 0.0001;
    skimmerGain.connect(master);
    this.skimmerGain = skimmerGain;
    this.skimmerOscillator = this.createContinuousOscillator('sine', 310, skimmerGain);
  }

  private get activeGain(): number {
    return Math.max(0.0001, this.volume * 0.58);
  }

  private createContinuousOscillator(type: OscillatorType, frequency: number, destination: AudioNode): OscillatorNode {
    if (!this.context) throw new Error('Audio context is not initialized.');
    const oscillator = this.context.createOscillator();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    oscillator.connect(destination);
    oscillator.start();
    return oscillator;
  }

  private playTone(
    startFrequency: number,
    endFrequency: number,
    duration: number,
    volume: number,
    type: OscillatorType,
    delay = 0,
  ): void {
    const context = this.context;
    const master = this.master;
    if (!context || !master || this.muted || this.paused) return;
    const start = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(startFrequency, start);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), start + duration);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(master);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.015);
    oscillator.addEventListener('ended', () => {
      oscillator.disconnect();
      gain.disconnect();
    }, { once: true });
  }

  private playNoise(duration: number, volume: number, cutoff: number): void {
    const context = this.context;
    const master = this.master;
    if (!context || !master || this.muted || this.paused) return;
    const length = Math.ceil(context.sampleRate * duration);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const channel = buffer.getChannelData(0);
    let state = 0x51f15e;
    for (let index = 0; index < length; index += 1) {
      state = (Math.imul(state, 1_664_525) + 1_013_904_223) >>> 0;
      channel[index] = (state / 4_294_967_296) * 2 - 1;
    }
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = buffer;
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    gain.gain.setValueAtTime(volume, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + duration);
    source.connect(filter).connect(gain).connect(master);
    source.start();
    source.stop(context.currentTime + duration);
    source.addEventListener('ended', () => {
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
    }, { once: true });
  }

  private playArpeggio(): void {
    for (const [index, frequency] of [220, 330, 440, 660].entries()) {
      this.playTone(frequency, frequency * 1.02, 0.12, 0.08, 'square', index * 0.085);
    }
  }
}
